/**
 * Live registry of the editor components (`knowledge-rte`,
 * `knowledge-whiteboard`) that can hold a KnowledgeObject edit lock, so a
 * component that is being replaced can tell whether ANOTHER connected
 * instance currently edits the same object before it gives the lock back.
 *
 * Why (review 2026-09-19, J1): the server only knows the USER as the holder,
 * not the tab or the element. Switching quickly between a whiteboard and a
 * text document swaps the component; the old element's late release
 * (`disconnectedCallback`, or the tail of an `_onEdit` that finished after it
 * was unmounted) would delete the lock the freshly mounted element for the
 * SAME object already relies on — it then shows edit mode without a real lock
 * and a second user can edit without a take-over dialog.
 *
 * Deliberately NOT covered: the same user with two browser TABS (tab 2 closing
 * releases tab 1's lock). This registry is per page; the root fix would be a
 * session id stored with the lock in the backend.
 */

export interface EditLockHolder {
    /** True while this connected element is in edit mode on `uid`. */
    holdsEditLockFor(uid: string): boolean;
}

const holders = new Set<EditLockHolder>();

export function registerEditLockHolder(holder: EditLockHolder): void {
    holders.add(holder);
}

export function unregisterEditLockHolder(holder: EditLockHolder): void {
    holders.delete(holder);
}

/** True when some registered element OTHER than `self` holds the lock for `uid`. */
export function anotherHolderHoldsLock(uid: string, self: EditLockHolder): boolean {
    for (const holder of holders) {
        if (holder !== self && holder.holdsEditLockFor(uid)) {
            return true;
        }
    }
    return false;
}
